"use client";

import React from "react";
import { motion } from "framer-motion";
import { Zap, House as Home, CircleArrowUp as ArrowUpCircle, Cpu, CircleCheck as CheckCircle2, Info } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
const categories = [{
  id: 1,
  title: "Electrical Repair",
  description: "Troubleshooting and repair for home electrical issues.",
  icon: "Zap"
}, {
  id: 2,
  title: "Home Wiring & Installation",
  description: "Code-compliant new circuits, switches, and fixtures.",
  icon: "Home"
}, {
  id: 3,
  title: "Electrical Upgrades",
  description: "Modernize your home's capacity for current technology.",
  icon: "ArrowUpCircle"
}, {
  id: 4,
  title: "Smart Home & Modern Tech",
  description: "Integrate modern controls and EV charging safely.",
  icon: "Cpu"
}];
const categoryFeatures = [{
  categoryId: 1,
  feature: "Circuit breaker trips diagnosis"
}, {
  categoryId: 1,
  feature: "Outlet and switch repair"
}, {
  categoryId: 1,
  feature: "Light fixture troubleshooting"
}, {
  categoryId: 1,
  feature: "Dangerous wiring repair"
}, {
  categoryId: 2,
  feature: "New outlet and switch installation"
}, {
  categoryId: 2,
  feature: "Dedicated circuits for appliances"
}, {
  categoryId: 2,
  feature: "Ceiling fan installation"
}, {
  categoryId: 2,
  feature: "Outdoor outlet installation"
}, {
  categoryId: 3,
  feature: "Main panel upgrade (100A → 200A)"
}, {
  categoryId: 3,
  feature: "Sub-panel installation"
}, {
  categoryId: 3,
  feature: "Aluminum-to-copper rewiring"
}, {
  categoryId: 3,
  feature: "Surge protection installation"
}, {
  categoryId: 4,
  feature: "Smart thermostat wiring"
}, {
  categoryId: 4,
  feature: "Home automation setup"
}, {
  categoryId: 4,
  feature: "EV charging station installation"
}, {
  categoryId: 4,
  feature: "Security camera wiring"
}];
const pricingData = [{
  id: 1,
  service: "Electrical Service Assessment",
  price: "Quote from your pro",
  note: "Ask about availability"
}, {
  id: 2,
  service: "Outlet/Switch Repair",
  price: "Quote from your pro",
  note: "Per location scope"
}, {
  id: 3,
  service: "Ceiling Fan Installation",
  price: "Quote from your pro",
  note: "Standard or custom ceiling height"
}, {
  id: 4,
  service: "Recessed Lighting",
  price: "Quote from your pro",
  note: "Based on number of fixtures"
}, {
  id: 5,
  service: "Panel Upgrade",
  price: "Quote from your pro",
  note: "Based on amperage and panel location"
}, {
  id: 6,
  service: "Full Home Rewiring",
  price: "Quote from your pro",
  note: "Requires on-site evaluation"
}];
const iconMap: Record<string, React.ElementType> = {
  Zap,
  Home,
  ArrowUpCircle,
  Cpu
};
const containerVariants = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  }
};
export default function ServiceCategoriesPricing() {
  return <section id="servicecategoriespricing" className="bg-background py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {}
        <div className="mb-16 text-center">
          <motion.div initial={{
          opacity: 0,
          y: -20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }}>
            <Badge variant="secondary" className="mb-4">
              Electrical Services
            </Badge>
            <h2 className="font-default text-foreground mb-4 text-3xl font-bold tracking-tight sm:text-4xl md:text-5xl">
              Professional Electrical Services
            </h2>
            <p className="text-muted-foreground mx-auto max-w-2xl text-lg">
              From circuit repairs to full home rewiring, independent local pros provide code-compliant electrical solutions for your home.
            </p>
          </motion.div>
        </div>

        {}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.1
      }} className="mb-20 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          {categories.map((category, i) => {
          const Icon = iconMap[category.icon];
          const features = categoryFeatures.filter(f => f.categoryId === category.id);
          return <motion.div key={category.id} variants={itemVariants} data-index={i}>
                <Card className="flex h-full flex-col shadow-sm transition-shadow hover:shadow-md">
                  <CardHeader>
                    <div className="bg-primary/10 text-primary mb-4 inline-flex h-12 w-12 items-center justify-center rounded-lg">
                      {Icon && <Icon className="h-6 w-6" />}
                    </div>
                    <CardTitle className="font-default text-xl">{category.title}</CardTitle>
                    <CardDescription>{category.description}</CardDescription>
                  </CardHeader>
                  <CardContent className="flex-1">
                    <ul className="space-y-3">
                      {features.map((feature, j) => <li key={j} className="flex items-start gap-2" data-index={j}>
                          <CheckCircle2 className="text-primary mt-0.5 h-4 w-4 shrink-0" />
                          <span className="text-muted-foreground text-sm">
                            {feature.feature}
                          </span>
                        </li>)}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.2
      }} className="mx-auto max-w-4xl">
          <div className="mb-8 text-center">
            <h3 className="font-default text-foreground mb-3 text-2xl font-bold sm:text-3xl">
              Transparent Electrical Pricing
            </h3>
            <p className="text-muted-foreground">
              Your independent pro provides a clear quote before any work starts.
            </p>
          </div>

          <Card className="overflow-hidden shadow-md">
            <Table>
              <TableHeader className="bg-muted/50">
                <TableRow>
                  <TableHead className="font-default w-[50%] text-base font-semibold">Service</TableHead>
                  <TableHead className="font-default text-base font-semibold">Estimated Price</TableHead>
                  <TableHead className="font-default text-right text-base font-semibold">Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pricingData.map((item, i) => <TableRow key={item.id} data-index={i}>
                    <TableCell className="font-medium">{item.service}</TableCell>
                    <TableCell className="text-foreground font-semibold">{item.price}</TableCell>
                    <TableCell className="text-muted-foreground text-right text-sm">
                      {item.note}
                    </TableCell>
                  </TableRow>)}
              </TableBody>
            </Table>
            <div className="bg-muted/30 border-t p-6 text-center sm:flex sm:items-center sm:justify-between sm:text-left">
              <div className="mb-4 flex items-center justify-center gap-2 sm:mb-0 sm:justify-start">
                <Info className="text-muted-foreground h-5 w-5" />
                <span className="text-muted-foreground text-sm">
                  Warranty terms are set by the pro — confirm before work begins. Work meets applicable codes.
                </span>
              </div>
              <Button asChild size="lg">
                <Link to="#bookingrequest">Request a Quote</Link>
              </Button>
            </div>
          </Card>
        </motion.div>
      </div>
    </section>;
}